// Sintesi del periodo per il report: solo conteggi e frasi costruite dai dati registrati
import { COUNTED_MEALS, dayKey } from './reportData';
import { weekdayName, time, minutesLabel } from './pdfReportKit';

const plural = (n, one, many) => (n === 1 ? one : many);
const MEAL_PLURAL = { Colazione: 'colazioni', Pranzo: 'pranzi', Cena: 'cene' };

const countBy = (items, keyFn) => items.reduce((acc, item) => {
  const k = keyFn(item);
  acc[k] = (acc[k] || 0) + 1;
  return acc;
}, {});

const dayMood = (moods) => {
  const c = countBy(moods, (m) => m.level);
  const best = ['serena', 'neutra', 'triste'].sort((a, b) => (c[b] || 0) - (c[a] || 0))[0];
  return c[best] ? best : null;
};

export const summarize = (model) => {
  const D = model.dayKeys.length;
  const periodWord = D <= 7 ? 'settimana' : 'periodo';
  const byDay = (items, field = 'time') => model.dayKeys.map((k) => items.filter((i) => i[field] && dayKey(i[field]) === k));

  const moodsUmore = model.moods.filter((m) => m.level);
  const dayMoods = byDay(moodsUmore).map(dayMood);
  const sereneDays = dayMoods.filter((m) => m === 'serena').length;
  const neutralDays = dayMoods.filter((m) => m === 'neutra').length;
  const sadDays = dayMoods.filter((m) => m === 'triste').length;
  const moodDays = sereneDays + neutralDays + sadDays;

  const dosesGiven = model.medicines.length;
  const dosesExpected = model.expectedDosesPerDay * D;
  const dosesByDay = byDay(model.medicines).map((d) => d.length);
  const shortDoseDays = model.expectedDosesPerDay
    ? model.dayKeys.filter((_, i) => dosesByDay[i] < model.expectedDosesPerDay) : [];

  const mainMeals = model.meals.filter((m) => COUNTED_MEALS.includes(m.type));
  const mealsExpected = COUNTED_MEALS.length * D;
  const mealsMissing = COUNTED_MEALS.map((type) => [type, D - Math.min(D, new Set(mainMeals.filter((m) => m.type === type).map((m) => dayKey(m.time))).size)])
    .filter(([, n]) => n > 0);
  const mealsGiven = Math.min(mealsExpected, mealsExpected - mealsMissing.reduce((s, [, n]) => s + n, 0));

  const allTimes = [...model.medicines, ...model.meals, ...model.activities, ...model.moods, ...model.notes, ...model.sos]
    .map((e) => e.time).concat(model.walks.map((w) => w.start));
  const activeDays = new Set(allTimes.map(dayKey)).size;
  const walkMinutes = model.walks.reduce((s, w) => s + (w.minutes || 0), 0);
  const cg = model.caregiverName || "L'assistente";

  const doseRate = dosesExpected ? Math.min(1, dosesGiven / dosesExpected) : null;
  let verdict = 'tranquilla';
  if (!allTimes.length) verdict = null;
  else if (sadDays >= 2 || model.sos.length >= 2 || (doseRate !== null && doseRate < 0.7)) verdict = 'impegnativa';
  else if (!model.sos.length && moodDays > 0 && sereneDays >= Math.ceil(moodDays * 0.6) && (doseRate === null || doseRate >= 0.9)) verdict = 'serena';

  const facts = [];
  if (allTimes.length) facts.push(activeDays === D ? `${cg} ha registrato attività tutti i giorni.` : `${cg} ha registrato attività in ${activeDays} giorni su ${D}.`);
  const counts = [];
  if (dosesExpected) counts.push(`medicine confermate ${dosesGiven} su ${dosesExpected}`);
  else if (dosesGiven) counts.push(`${dosesGiven} ${plural(dosesGiven, 'somministrazione registrata', 'somministrazioni registrate')}`);
  if (allTimes.length) counts.push(`pasti principali registrati ${mealsGiven} su ${mealsExpected}`);
  if (model.walks.length) counts.push(`${model.walks.length} ${plural(model.walks.length, 'passeggiata', 'passeggiate')}`);
  if (counts.length) facts.push(`${counts.join('; ').replace(/^./, (c) => c.toUpperCase())}.`);
  if (model.sos.length === 1) facts.push(`Un allarme Scudo ${weekdayName(model.sos[0].time).toLowerCase()} alle ${time(model.sos[0].time)}.`);
  else if (model.sos.length > 1) facts.push(`${model.sos.length} allarmi Scudo nel ${periodWord}.`);

  const watch = [];
  if (shortDoseDays.length) {
    const missing = Math.max(0, dosesExpected - dosesGiven);
    const names = shortDoseDays.slice(0, 3).map((k) => weekdayName(new Date(`${k}T12:00:00Z`)).toLowerCase());
    watch.push(`${missing || shortDoseDays.length} ${plural(missing, 'dose non confermata', 'dosi non confermate')} (${names.join(', ')}${shortDoseDays.length > 3 ? '…' : ''}).`);
  }
  if (mealsMissing.length) watch.push(`Pasti non registrati: ${mealsMissing.map(([t, n]) => `${n} ${n === 1 ? t.toLowerCase() : MEAL_PLURAL[t]}`).join(', ')}.`);
  if (sadDays) watch.push(`Umore triste in ${sadDays} ${plural(sadDays, 'giornata', 'giornate')}.`);
  if (model.sos.length) watch.push(`${model.sos.length === 1 ? 'Un allarme Scudo' : `${model.sos.length} allarmi Scudo`}: vale la pena parlarne con ${model.caregiverName || "l'assistente"}.`);

  return {
    D, periodWord, verdict, facts, watch, dayMoods, sereneDays, neutralDays, sadDays, moodDays,
    dosesGiven, dosesExpected, mealsGiven, mealsExpected, mealsMissing, walkMinutes,
    walkSummary: `${model.walks.length} ${plural(model.walks.length, 'uscita', 'uscite')} · ${minutesLabel(walkMinutes)} all'aperto`,
    mealPlural: MEAL_PLURAL,
  };
};
